"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect, useRef, type ReactNode } from "react";
import { X } from "lucide-react";
import { cn } from "@/utils/cn";
export interface TutorialTooltipProps {
  /** Unique identifier for this tutorial - used for localStorage tracking */
  tutorialId: string;
  /** The content to display in the tutorial */
  children: ReactNode;
  /** Position of the tooltip relative to the target */
  position?: "top" | "bottom" | "left" | "right" | "auto";
  /** Whether to show the tutorial (controlled mode) */
  isOpen?: boolean;
  /** Callback when tutorial is dismissed */
  onDismiss?: () => void;
  /** Additional CSS classes */
  className?: string;
  /** Offset from the target element in pixels */
  offset?: number;
}
const TUTORIAL_DISMISSED_PREFIX = "tutorial_dismissed_";
export function TutorialTooltip(t0) {
  const $ = _c(40);
  const {
    tutorialId,
    children,
    position: t1,
    isOpen: controlledIsOpen,
    onDismiss,
    className
  } = t0;
  const position = t1 === undefined ? "auto" : t1;
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [calculatedPosition, setCalculatedPosition] = useState("top");
  const tooltipRef = useRef(null);
  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;
  let t2;
  let t3;
  if ($[0] !== controlledIsOpen || $[1] !== tutorialId) {
    t2 = () => {
      if (typeof window === "undefined") {
        return;
      }
      const isDismissed = localStorage.getItem(`${TUTORIAL_DISMISSED_PREFIX}${tutorialId}`) === "true";
      if (!isDismissed && controlledIsOpen === undefined) {
        setInternalIsOpen(true);
      }
    };
    t3 = [tutorialId, controlledIsOpen];
    $[0] = controlledIsOpen;
    $[1] = tutorialId;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[4] !== isOpen || $[5] !== position) {
    t4 = () => {
      if (!isOpen || position !== "auto" || !tooltipRef.current) {
        return;
      }
      const tooltip = tooltipRef.current;
      const parent = tooltip.parentElement;
      if (!parent) {
        return;
      }
      const parentRect = parent.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const spaceTop = parentRect.top;
      const spaceBottom = viewportHeight - parentRect.bottom;
      const spaceLeft = parentRect.left;
      const spaceRight = viewportWidth - parentRect.right;
      const spaces = {
        top: spaceTop,
        bottom: spaceBottom,
        left: spaceLeft,
        right: spaceRight
      };
      const bestPosition = (Object.keys(spaces) as Array<keyof typeof spaces>).reduce((a, b) => spaces[a] > spaces[b] ? a : b);
      setCalculatedPosition(bestPosition);
    };
    t5 = [isOpen, position];
    $[4] = isOpen;
    $[5] = position;
    $[6] = t4;
    $[7] = t5;
  } else {
    t4 = $[6];
    t5 = $[7];
  }
  useEffect(t4, t5);
  let t6;
  if ($[8] !== onDismiss || $[9] !== tutorialId) {
    t6 = () => {
      if (typeof window !== "undefined") {
        localStorage.setItem(`${TUTORIAL_DISMISSED_PREFIX}${tutorialId}`, "true");
      }
      setInternalIsOpen(false);
      onDismiss?.();
    };
    $[8] = onDismiss;
    $[9] = tutorialId;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const handleDismiss = t6;
  if (!isOpen) {
    return null;
  }
  const finalPosition = position === "auto" ? calculatedPosition : position;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t7;
  let t8;
  let t9;
  if ($[11] !== children || $[12] !== className || $[13] !== finalPosition || $[14] !== handleDismiss) {
    const positionClasses = {
      top: "bottom-full left-1/2 -translate-x-1/2 mb-3",
      bottom: "top-full left-1/2 -translate-x-1/2 mt-3",
      left: "right-full top-1/2 -translate-y-1/2 mr-3",
      right: "left-full top-1/2 -translate-y-1/2 ml-3"
    };
    const arrowClasses = {
      top: "top-full left-1/2 -translate-x-1/2 border-l-transparent border-r-transparent border-b-transparent border-t-primary",
      bottom: "bottom-full left-1/2 -translate-x-1/2 border-l-transparent border-r-transparent border-t-transparent border-b-primary",
      left: "left-full top-1/2 -translate-y-1/2 border-t-transparent border-b-transparent border-r-transparent border-l-primary",
      right: "right-full top-1/2 -translate-y-1/2 border-t-transparent border-b-transparent border-l-transparent border-r-primary"
    };
    t8 = tooltipRef;
    t9 = cn("absolute z-[9999] bg-primary text-white rounded-lg shadow-xl p-4 w-64 max-w-[calc(100vw-2rem)] animate-in fade-in-0 zoom-in-95", positionClasses[finalPosition], className);
    t10 = "dialog";
    t11 = "Tutorial";
    if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = {
        maxWidth: "calc(100vw - 2rem)"
      };
      $[23] = t12;
    } else {
      t12 = $[23];
    }
    let t15;
    if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = <X className="w-4 h-4" />;
      $[24] = t15;
    } else {
      t15 = $[24];
    }
    if ($[25] !== handleDismiss) {
      t13 = <button onClick={handleDismiss} className="absolute top-2 right-2 text-white hover:bg-white/20 rounded-full p-1 transition-colors" aria-label="Dismiss tutorial">{t15}</button>;
      $[25] = handleDismiss;
      $[26] = t13;
    } else {
      t13 = $[26];
    }
    if ($[27] !== children) {
      t14 = <div className="pr-6">{children}</div>;
      $[27] = children;
      $[28] = t14;
    } else {
      t14 = $[28];
    }
    t7 = cn("absolute w-0 h-0 border-8", arrowClasses[finalPosition]);
    $[11] = children;
    $[12] = className;
    $[13] = finalPosition;
    $[14] = handleDismiss;
    $[15] = t10;
    $[16] = t11;
    $[17] = t12;
    $[18] = t13;
    $[19] = t14;
    $[20] = t7;
    $[21] = t8;
    $[22] = t9;
  } else {
    t10 = $[15];
    t11 = $[16];
    t12 = $[17];
    t13 = $[18];
    t14 = $[19];
    t7 = $[20];
    t8 = $[21];
    t9 = $[22];
  }
  let t15;
  if ($[29] !== t7) {
    t15 = <div className={t7} aria-hidden="true" />;
    $[29] = t7;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== t10 || $[32] !== t11 || $[33] !== t12 || $[34] !== t13 || $[35] !== t14 || $[36] !== t15 || $[37] !== t8 || $[38] !== t9) {
    t16 = <div ref={t8} className={t9} role={t10} aria-label={t11} style={t12}>{t13}{t14}{t15}</div>;
    $[31] = t10;
    $[32] = t11;
    $[33] = t12;
    $[34] = t13;
    $[35] = t14;
    $[36] = t15;
    $[37] = t8;
    $[38] = t9;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  return t16;
}

/**
 * Hook to check if a tutorial has been dismissed
 */
export function useTutorialDismissed(tutorialId) {
  const $ = _c(3);
  const [isDismissed, setIsDismissed] = useState(false);
  let t0;
  let t1;
  if ($[0] !== tutorialId) {
    t0 = () => {
      if (typeof window === "undefined") {
        return;
      }
      const dismissed = localStorage.getItem(`${TUTORIAL_DISMISSED_PREFIX}${tutorialId}`) === "true";
      setIsDismissed(dismissed);
    };
    t1 = [tutorialId];
    $[0] = tutorialId;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  return isDismissed;
}

/**
 * Reset a tutorial (for development/testing)
 */
export function resetTutorial(tutorialId: string) {
  if (typeof window === "undefined") return;
  localStorage.removeItem(`${TUTORIAL_DISMISSED_PREFIX}${tutorialId}`);
}